/**
 * 打印小票。
 *
 * ★ 先给**看得见的预览**,再打。
 *   小票打错的成本不是一张纸 —— 是一箱贴错单的货,
 *   而贴错这件事要到司机送到别人家门口才会被发现。
 *
 * ★ 打印方式和纸宽存在这台机器上(localStorage),不进服务端配置:
 *   「哪台打印机、多宽的纸」是**这台电脑**的事,
 *   仓库那台和办公室那台本来就不一样。
 */
import { useEffect, useRef, useState } from 'react';
import {
  buildReceipt,
  DEFAULT_RECEIPT_SIZES,
  type ReceiptOrder,
  type ReceiptSize,
  type ReceiptSizes,
  type ReceiptSlot,
} from '@delivery/shared';
import { api } from '../lib/api';
import { isOnline, type PrinterRow } from './PrinterSettings';
import {
  bluetoothSupported,
  connectedPrinterName,
  loadPrinterSettings,
  pickPrinter,
  printReceipts,
  renderReceipt,
  savePrinterSettings,
  type PrinterSettings,
} from '../lib/printer';

/** 票面上的几块,按人读它的顺序排 */
const SLOTS: Array<[ReceiptSlot, string]> = [
  /*
   * ★ 二维码印不印,由**这儿**决定,不在商家资料里。
   *
   *   商家那边只负责「填什么」(他的收款码 / 公众号),这边决定
   *   「这叠票上印不印、印多大」—— 同一个商家,贴货架的票要印码,
   *   内部留存那张不需要;开关放在商家资料里的话,
   *   他得先去改商家、打完再改回来。
   *
   *   默认「不印」:填了内容不等于每张票都要印(见 DEFAULT_RECEIPT_SIZES)。
   */
  ['qr', '商家二维码'],
  ['shop', '商家别名'],
  ['name', '收件人'],
  ['phone', '电话'],
  ['address', '地址'],
  ['special', '特殊物品'],
  ['cod', '代收现金'],
  ['count', '件数'],
  ['remark', '备注'],
  ['orderNo', '单号'],
  ['zone', '区域'],
  ['time', '时间'],
  ['tail', '底部留白'],
];

const SIZES: Array<[ReceiptSize, string]> = [
  /*
   * ★ 「不印」排第一。纸上那几行里总有几样是这家店不看的
   *   (有的店不看区域,有的不看单号)—— 而印一行没人看的字,
   *   占的是本来能留给手写的地方。
   */
  ['off', '不印'],
  ['small', '小'],
  ['normal', '标准'],
  ['big', '大'],
  ['huge', '特大'],
  ['giant', '超大'],
];

export function PrintDialog({
  orders,
  onClose,
  onPrinted,
}: {
  orders: ReceiptOrder[];
  onClose: () => void;
  /**
   * 打完了 —— 让列表上的 🖨 标记当场出现。
   *
   * ★ 不报的话,人得刷新一次才看得到哪几单打过了,
   *   而「只看没打过的」正是他接着要按的那个筛选。
   */
  onPrinted?: () => void;
}) {
  const [settings, setSettings] = useState<PrinterSettings>(() => loadPrinterSettings());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [printer, setPrinter] = useState<string | null>(() => connectedPrinterName());
  /** 设置里配好的那些后台打印机 —— 有就把「直接出纸」这条路摆出来 */
  const [servers, setServers] = useState<PrinterRow[]>([]);
  const holder = useRef<HTMLDivElement>(null);

  useEffect(() => savePrinterSettings(settings), [settings]);

  /*
   * ★ 纸宽和字号是**这家店**的事,不是「我这台设备的偏好」——
   *   所以从服务端读,改了也存回服务端。
   *
   *   原来只存在浏览器里,于是「设一次」只对那台设备有效:
   *   老板在手机上调好字号,仓库那台电脑打出来还是默认的那张。
   *
   *   「用哪台打印机」「走蓝牙还是系统打印」仍然是设备自己的事,
   *   那两项照旧存在本机。
   */
  /*
   * ★ 人已经动过手了,就**别拿服务端那份盖回去**。
   *
   *   这一请求是异步的:人打开弹窗就去调字号,而它回来得晚一步 ——
   *   回来那一刻把刚调好的盖成了店里存的旧值。
   *   现象是「我明明选了超大,打出来还是原来的」,而且时快时慢地发生。
   */
  const touched = useRef(false);
  useEffect(() => {
    /*
     * ★ 这台设备自己调过排版的话,**不拿店里那份盖回去**。
     *   盖回去的话,没权限改店铺设置的人(客服)在这台手机上调完,
     *   下次打开又变回去 —— 现象是「我明明设过,每次都得重设一遍」。
     */
    if (settings.layoutLocal) return;
    void api<{ paper: '58' | '80'; sizes: ReceiptSizes }>('/printers/layout')
      .then((r) => {
        if (touched.current) return;
        setSettings((s) => ({ ...s, paper: r.paper, sizes: r.sizes ?? {} }));
      })
      .catch(() => undefined);
    // 只在打开这一刻判断一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 改了排版:先记在这台设备上(一定成),再试着存回店里(要权限)。
   *
   * ★ 两边都存的理由:存店里是为了「设一次,谁打都一样」,
   *   而存本机是为了「没权限的人也能调,而且调完不会被盖回去」。
   */
  const saveLayout = (paper: PrinterSettings['paper'], sizes: ReceiptSizes) => {
    touched.current = true;
    void api('/printers/layout', { method: 'PATCH', body: { paper, sizes } }).catch(
      () => undefined,
    );
  };

  /*
   * ★ 没配后台打印机时,这条路**根本不出现** ——
   *   摆一个选了也没用的选项,比不摆更让人困惑。
   *   401/403 也当成「没有」:客服看不到设置,但照样要能打印。
   */
  /**
   * 记着的那台打印机已经不在了 —— 这一屏要说出来。
   *
   * ★ 用户报的:「删除打印机,配对新的以后,打印提示打印机不在了,
   *   但是设置里面显示这个打印机在线」。
   *
   *   两边说的是**不同的打印机**:设置页看到的是新配的那台(在线),
   *   而这个弹窗记着的还是被删掉那台的 id —— 它只在切到「直接出纸」
   *   那一下才补默认值(`s.serverPrinterId ?? servers[0].id`),
   *   而里面存的不是空,`??` 就永远保留它。列表回来时**没有人核对
   *   那个 id 还在不在**,于是这台设备上再也打不出来,
   *   而人看到的两句话互相矛盾。
   */
  const [switched, setSwitched] = useState<string | null>(null);

  useEffect(() => {
    void api<{ data: PrinterRow[] }>('/printers')
      .then((r) => {
        const live = r.data.filter((p) => p.isActive);
        setServers(live);

        /*
         * ★ 核对一下记着的那台还在不在。
         *
         *   判断写在更新函数**外面**:在 setState 的更新函数里做副作用
         *   (这里是再调一个 setState)不可靠 —— 严格模式下更新函数会被
         *   调用两次,而且 React 不保证它只跑一次。
         *   第一版就是这么写的,结果那行提示压根不出现。
         */
        const remembered = loadPrinterSettings().serverPrinterId;
        if (!remembered || live.some((p) => p.id === remembered)) return;

        /*
         * ★ 只有一台的时候直接换过去,多台就清空让人自己选。
         *
         *   一台的时候「换成它」不可能错;而有好几台时替人选一台
         *   等于把小票发到别的柜台 —— 那种错发出去就收不回来了。
         *
         * ★ 换了要**说出来**(下面那行提示)。默默换掉的话,
         *   下一次小票从另一台机器出来,没人知道为什么。
         */
        const only = live.length === 1 ? live[0]! : null;
        setSwitched(only ? only.name : '');
        setSettings((s) => ({ ...s, serverPrinterId: only ? only.id : null }));
      })
      .catch(() => setServers([]));
  }, []);

  /*
   * ★ 预览用的是**和蓝牙打印一模一样那张图**(同一个 renderReceipt)。
   *   另外写一套预览的话,两边迟早对不上 ——
   *   而那时候人相信的是屏幕,出错的是纸。
   */
  const first = orders[0];
  /*
   * ★ 画这张预览现在是**异步**的:图片那种二维码要先解码
   *   (见 renderReceipt 里的说明)。
   *
   *   所以要挡住竞态 —— 人连着拨两下字号,两次渲染都在跑,
   *   而先发的那次可能后回来。`alive` 保证只有最后一次的结果落到屏幕上;
   *   没有它的话现象是「拨回去一档,预览却停在上一档」,
   *   而人会以为是设置没生效。
   */
  /*
   * ★ 「开了但没填」是个很容易撞上的状态,所以单独算出来提示(见下面那段)。
   *   两个条件缺一不印:弹窗里选了尺寸、而且商家资料里填了内容。
   */
  const qrOn = (settings.sizes['qr'] ?? DEFAULT_RECEIPT_SIZES['qr']) !== 'off';
  const anyShopQr = orders.some((o) => o.shopQr);

  const [preview, setPreview] = useState<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (!first) {
      setPreview(null);
      return;
    }
    let alive = true;
    void renderReceipt(
      buildReceipt(first, { now: new Date(), sizes: settings.sizes }),
      settings.paper,
    ).then((c) => {
      if (alive) setPreview(c);
    });
    return () => {
      alive = false;
    };
  }, [first, settings.paper, settings.sizes]);
  useEffect(() => {
    const box = holder.current;
    if (!box || !preview) return;
    box.replaceChildren(preview);
    preview.style.width = '100%';
    preview.style.height = 'auto';
  }, [preview]);

  async function connect() {
    setErr(null);
    try {
      setPrinter(await pickPrinter());
    } catch (e) {
      // 人自己按了「取消」不算错 —— 弹一句红字只会让他以为坏了
      const msg = e instanceof Error ? e.message : '连接失败';
      if (!/cancel|User cancelled/i.test(msg)) setErr(msg);
    }
  }

  async function print() {
    setBusy(true);
    setErr(null);
    setOk(null);
    try {
      /*
       * ★ 走后台打印时,**先确认这台打印机还在**。
       *
       *   用户报的:打印台上一张打不出来,报「URI malformed」。
       *   根因就在这一行:格式是按「这台是什么类型」选的,而选中的那台
       *   一旦在列表里找不着(那台被删了重新配对、id 变了;或者列表还没加载完),
       *   类型就是 undefined —— 于是按字节发,而浏览器打印台吃不了字节。
       *
       *   找不着就**当场说清楚**,别发出去等它在收银机上失败:
       *   那头的报错人看不到,他只知道纸没出来。
       */
      const picked = servers.find((p) => p.id === settings.serverPrinterId);
      if (settings.way === 'server' && !picked) {
        throw new Error(
          servers.length === 0
            ? '打印机列表还没加载出来 —— 稍等一下再按,或者先用「系统打印」'
            : '之前选的那台打印机不在了(可能被删了或重新配对过)—— 在下面重新选一台',
        );
      }
      await printReceipts(orders, settings, { serverPrinterKind: picked?.kind });
      setOk(
        settings.way === 'bluetooth'
          ? `已发给打印机 ${orders.length} 张`
          : settings.way === 'server'
            ? `已发到打印机 ${orders.length} 张 —— 马上出纸`
            : `已送到打印对话框 ${orders.length} 张`,
      );
      onPrinted?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '打印失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-label="打印小票"
        className="max-h-[90dvh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5
                   dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          打印小票 · {orders.length} 张
        </h2>

        <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto]">
          <div className="space-y-4">
            <div>
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200">怎么打</div>
              <div className="mt-1.5 space-y-1.5">
                {servers.length > 0 && (
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      aria-label="直接出纸"
                      className="mt-1"
                      checked={settings.way === 'server'}
                      onChange={() =>
                        setSettings((s) => ({
                          ...s,
                          way: 'server',
                          serverPrinterId: s.serverPrinterId ?? servers[0]!.id,
                        }))
                      }
                    />
                    <span className="min-w-0">
                      <span className="text-slate-800 dark:text-slate-100">直接出纸</span>
                      <span className="block text-xs text-slate-500">
                        不弹打印框,手机和 iPad 上也一样。任务发到打印机那边,它自己来取
                      </span>
                      {settings.way === 'server' && (
                        <span className="mt-1 flex flex-wrap items-center gap-2">
                          <select
                            aria-label="选打印机"
                            value={settings.serverPrinterId ?? ''}
                            onChange={(e) =>
                              setSettings((s) => ({ ...s, serverPrinterId: e.target.value }))
                            }
                            className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs
                                       dark:border-slate-600 dark:bg-slate-800"
                          >
                            {/*
                              ★ 没选中时要有一个**看得见的空选项**。
                                没有它的话,value 对不上任何 option 时浏览器
                                显示的是第一台 —— 看起来已经选好了,
                                而按下去报「还没选打印机」。
                            */}
                            {!settings.serverPrinterId && <option value="">请选一台…</option>}
                            {servers.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </select>
                          {/*
                            ★ 离线时要**当场**说出来。
                              不说的话人按下打印、纸没出来,他会再按几次 ——
                              而那几张都老老实实排在队列里,等那头一上线全吐出来。
                          */}
                          {(() => {
                            const pick = servers.find((p) => p.id === settings.serverPrinterId);
                            if (!pick) return null;
                            return isOnline(pick) ? (
                              <span className="text-xs text-green-600 dark:text-green-400">
                                在线
                              </span>
                            ) : (
                              <span className="text-xs text-amber-600 dark:text-amber-400">
                                离线 —— 现在按会排队,那头一上线就打
                              </span>
                            );
                          })()}
                        </span>
                      )}
                    </span>
                  </label>
                )}
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    aria-label="系统打印"
                    className="mt-1"
                    checked={settings.way === 'system'}
                    onChange={() => setSettings((s) => ({ ...s, way: 'system' }))}
                  />
                  <span>
                    <span className="text-slate-800 dark:text-slate-100">系统打印</span>
                    <span className="block text-xs text-slate-500">
                      弹一次打印对话框。装在这台电脑上的打印机都能用 ——
                      USB、网口、已经配对好的蓝牙机
                    </span>
                    {/*
                      ★ 这一条值得摆在界面上,而不是埋在文档里。
                        仓库里那台电脑(或者收银机)上本来就接好了打印机,
                        而「不想每次弹框」是人当场就会问的下一个问题 ——
                        答案只是给 Chrome 的快捷方式加一个开关,什么都不用装。
                    */}
                    {settings.way === 'system' && (
                      <span
                        className="mt-1 block rounded bg-slate-50 px-2 py-1 text-[11px]
                                       text-slate-500 dark:bg-slate-900/60"
                      >
                        不想弹框?给这台电脑的 Chrome 快捷方式加上{' '}
                        <code className="font-mono">--kiosk-printing</code>
                        ,再把小票机设成默认打印机 —— 之后按一下直接出纸,不用装任何东西
                      </span>
                    )}
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    aria-label="蓝牙直连"
                    className="mt-1"
                    disabled={!bluetoothSupported()}
                    checked={settings.way === 'bluetooth'}
                    onChange={() => setSettings((s) => ({ ...s, way: 'bluetooth' }))}
                  />
                  <span>
                    <span className="text-slate-800 dark:text-slate-100">蓝牙直连</span>
                    <span className="block text-xs text-slate-500">
                      {bluetoothSupported()
                        ? '一按出纸,不弹对话框。要先连上打印机'
                        : '这个浏览器没有蓝牙功能(iPhone 上的浏览器都没有)—— 用系统打印'}
                    </span>
                  </span>
                </label>
              </div>
              {settings.way === 'bluetooth' && (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={() => void connect()}
                    className="rounded-lg border border-brand-600 px-3 py-1.5 text-xs
                               font-medium text-brand-600 dark:text-brand-400"
                  >
                    {printer ? '换一台' : '连接打印机'}
                  </button>
                  <span className="text-xs text-slate-500">
                    {printer ? `已连 ${printer}` : '还没连打印机'}
                  </span>
                </div>
              )}
            </div>

            <div>
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200">纸多宽</div>
              <div className="mt-1.5 flex gap-3">
                {(['80', '58'] as const).map((p) => (
                  <label key={p} className="flex items-center gap-1.5 text-sm">
                    <input
                      type="radio"
                      aria-label={`${p}mm`}
                      checked={settings.paper === p}
                      onChange={() =>
                        setSettings((s) => {
                          saveLayout(p, s.sizes);
                          return { ...s, paper: p, layoutLocal: true };
                        })
                      }
                    />
                    {p}mm
                  </label>
                ))}
              </div>
            </div>

            {/*
              ★ 字号**按块**调,不是一个「大中小」拉到底。
                同一叠票在不同店里的用法不一样:有人代收多,
                要那一行隔着几米看得见;有人地址长,宁可地址大、别的缩掉。
                一个全局档位答不了这件事。
            */}
            <div>
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200">字号</div>
              <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1.5">
                {SLOTS.map(([slot, label]) => (
                  <label key={slot} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-slate-600 dark:text-slate-300">{label}</span>
                    <select
                      aria-label={`${label}字号`}
                      value={settings.sizes[slot] ?? DEFAULT_RECEIPT_SIZES[slot]}
                      onChange={(e) =>
                        setSettings((s) => {
                          const sizes = { ...s.sizes, [slot]: e.target.value as ReceiptSize };
                          saveLayout(s.paper, sizes);
                          return { ...s, sizes, layoutLocal: true };
                        })
                      }
                      className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs
                                 dark:border-slate-600 dark:bg-slate-800"
                    >
                      {SIZES.map(([v, n]) => (
                        <option key={v} value={v}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              {/*
                ★ 开了二维码、而这几单的商家一个都没填内容时,当场说出来。
                
                  不说的话人会打完一叠才发现码没印上,而他第一反应是
                  「这个开关坏了」或者「打印机没印全」—— 他不会想到
                  要去商家资料里看有没有填。两个条件缺一不印,
                  那就得让他知道缺的是哪一个。
              */}
              {qrOn && !anyShopQr && (
                <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
                  已经打开二维码,但这几单的商家都还没填内容 —— 去「设置 → 商家」
                  里填上(网址或者一张图),这里才印得出来。
                </p>
              )}
              <button
                onClick={() =>
                  setSettings((s) => {
                    saveLayout(s.paper, {});
                    return { ...s, sizes: {}, layoutLocal: true };
                  })
                }
                className="mt-1.5 text-xs text-slate-500 hover:underline"
              >
                恢复默认字号
              </button>
            </div>

            {/*
              ★ 「记着的那台不在了」要在**按打印之前**就看得见,而且
                **不能**放在「直接出纸」那一块里面 —— 那一块只有
                `servers.length > 0` 才渲染,于是一台打印机都不剩的时候
                这句话反而不出现,而那正是最需要它的情况。
                (第一版就放错了地方,用例当场抓住。)
            */}
            {switched !== null && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm
                           text-amber-800 dark:border-amber-900 dark:bg-amber-950/30
                           dark:text-amber-300"
              >
                {switched
                  ? `之前选的那台打印机不在了(被删或重新配对过)—— 已经换成「${switched}」`
                  : servers.length > 0
                    ? '之前选的那台打印机不在了(被删或重新配对过)—— 下面重新选一台'
                    : '之前选的那台打印机不在了(被删或重新配对过),而现在一台可用的都没有 —— 去「设置 → 打印机」重新配一台'}
              </div>
            )}
            {err && (
              <div
                className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700
                              dark:bg-red-950/40 dark:text-red-300"
              >
                {err}
              </div>
            )}
            {ok && (
              <div
                aria-live="polite"
                className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800
                           dark:bg-green-950/40 dark:text-green-300"
              >
                {ok}
              </div>
            )}
          </div>

          {/* 预览 —— 第一张长什么样,纸上就是什么样 */}
          <div className="w-full sm:w-56">
            <div className="text-xs text-slate-500">
              预览{orders.length > 1 && `(第 1 张,共 ${orders.length} 张)`}
            </div>
            <div
              ref={holder}
              aria-label="小票预览"
              className="mt-1.5 overflow-hidden rounded border border-slate-300 bg-white
                         dark:border-slate-600"
            />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-500">
            关闭
          </button>
          <button
            onClick={() => void print()}
            disabled={busy || orders.length === 0}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white
                       disabled:opacity-40"
          >
            {busy ? '打印中…' : `打印 ${orders.length} 张`}
          </button>
        </div>
      </div>
    </div>
  );
}
